import React, { useState, useEffect } from 'react';
import { User, Lock, Eye, EyeOff, Loader2, Stethoscope, Heart, Shield, Activity, Sun, Moon } from 'lucide-react';
import ClinicLogo from './ClinicLogo';
import ChangePasswordModal from './ChangePasswordModal';
import { authAPI } from '../services/api';
import useTheme from '../hooks/useTheme';
import { isNewVersionDeployed, reloadToLatestVersion } from '../utils/appVersion';

const Login = ({ onLoginSuccess }) => {
  const { resolvedTheme, setTheme } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState(null);

  useEffect(() => {
    // Trigger mount animations after a brief delay
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    try {
      const data = await authAPI.login(username.trim(), password);

      // Check if password change is required
      if (data.requirePasswordChange) {
        setLoginCredentials({ username: username.trim(), password });
        setShowChangePassword(true);
        return;
      }

      // Store auth data
      localStorage.setItem('clinic_auth_token', data.token);
      localStorage.setItem('clinic_auth_user', JSON.stringify(data.user));

      // Signing in is the one safe moment to pick up a new build: nothing is half-written, and the
      // session was just persisted above, so the reload lands the user straight back here on the
      // latest version. Without this, a tab left open across a deploy keeps running the old code,
      // because signing in changes state rather than reloading the document.
      if (await isNewVersionDeployed()) {
        reloadToLatestVersion();
        return;
      }

      onLoginSuccess(data.user, data.token);
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChanged = (user, token) => {
    setShowChangePassword(false);
    setLoginCredentials(null);
    onLoginSuccess(user, token);
  };

  if (showChangePassword && loginCredentials) {
    return (
      <ChangePasswordModal
        username={loginCredentials.username}
        currentPassword={loginCredentials.password}
        onSuccess={handlePasswordChanged}
      />
    );
  }

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}>
      {/* Keyframe animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(60px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes glowPulse {
          0%, 100% {
            text-shadow:
              0 0 10px rgba(230, 180, 75, 0.4),
              0 0 30px rgba(217, 157, 31, 0.2),
              0 0 60px rgba(200, 136, 0, 0.1);
          }
          50% {
            text-shadow:
              0 0 20px rgba(230, 180, 75, 0.6),
              0 0 50px rgba(217, 157, 31, 0.35),
              0 0 80px rgba(200, 136, 0, 0.2);
          }
        }
        @keyframes shimmerBar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .urdu-animated {
          opacity: 0;
          animation:
            slideInRight 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards,
            float 5s ease-in-out 1.5s infinite,
            glowPulse 3s ease-in-out 1.5s infinite;
          color: #ffffff;
        }
        .fade-in-up {
          opacity: 0;
          animation: fadeInUp 0.8s ease-out forwards;
        }
      `}</style>

      {/* Left Panel — Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 flex-col justify-between p-12">
        {/* A faint dot grid, nothing more: the logo carries the colour. */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '32px 32px'
            }}
          />
        </div>

        {/* The small logo-and-label that sat here repeated the large mark and name below; removed
            (owner, 2026-10-01). The empty block keeps the panel's top / middle / bottom spacing, so
            the name stays centred and the footer at the bottom. */}
        <div aria-hidden="true" />

        {/* Center — Hero text */}
        <div className="relative z-10 -mt-4">
          {/* The mark with the hospital's name beside it: English first and large, the Urdu
              beneath (owner, 2026-10-01). */}
          <div className="flex items-center gap-7 mb-8">
            {/* The mark straight on the navy, reversed so its navy letters and figure show in white
                (ClinicLogo variant "white"); no white disc behind it (owner, 2026-10-02). */}
            <div className="fade-in-up flex-shrink-0" style={{ animationDelay: '0.3s' }}>
              <ClinicLogo size={136} variant="white" />
            </div>
            <div className="min-w-0">
              <h1
                className="fade-in-up text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight"
                style={{ animationDelay: '0.45s' }}
              >
                Perveen Aslam Memorial Hospital
              </h1>
              {/* Animated Urdu name. Left-aligned under the English: .urdu-text is right-aligned
                  app-wide (App.css), so the alignment is set inline here. */}
              <div className="relative inline-block mt-1">
                <p
                  className="urdu-text text-3xl xl:text-4xl text-white urdu-animated"
                  style={{ lineHeight: '1.9', textAlign: 'left' }}
                >
                  پروین اسلم میموریل ہسپتال
                </p>
                {/* Shimmer bar that sweeps across the text */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-lg" style={{ opacity: 0, animation: 'fadeInUp 0.1s ease-out 1.3s forwards' }}>
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 45%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.15) 55%, transparent 100%)',
                      animation: 'shimmerBar 3s ease-in-out 2s infinite',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            className="fade-in-up"
            style={{ animationDelay: '0.6s' }}
          >
            {/* The logo's four colours, in the order its figures stand. */}
            <div className="flex w-28 h-1 rounded-full overflow-hidden mb-6" aria-hidden="true">
              <span className="flex-1 bg-[#809fd4]" />
              <span className="flex-1 bg-[#d99d1f]" />
              <span className="flex-1 bg-[#2fae63]" />
              <span className="flex-1 bg-[#1ab0d1]" />
            </div>
            <p className="text-navy-200 text-base leading-relaxed max-w-md">
              Comprehensive hospital management - patient records, prescriptions, queue management, and inventory - all in one place.
            </p>
          </div>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-3 mt-8">
            {[
              { icon: Stethoscope, label: 'Prescriptions' },
              { icon: Heart, label: 'Patient Care' },
              { icon: Shield, label: 'Secure Records' },
              { icon: Activity, label: 'Live Queue' },
            ].map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                className="flex items-center space-x-2 bg-white/10 border border-white/10 rounded-full px-4 py-2 fade-in-up"
                style={{ animationDelay: `${0.8 + i * 0.15}s` }}
              >
                <Icon className="w-4 h-4 text-gold-300" />
                <span className="text-navy-100 text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom — Footer */}
        <div className="relative z-10">
          <div className="border-t border-white/10 pt-6">
            <p className="text-navy-300 text-sm">
              &copy; {new Date().getFullYear()} Perveen Aslam Memorial Hospital
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 px-6 py-12 relative">
        {/* Theme toggle */}
        <button
          type="button"
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          className="absolute top-5 right-5 z-20 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-navy-700 hover:border-navy-300 transition-all duration-200"
          title={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {resolvedTheme === 'dark' ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
        </button>

        {/* The logo's band along the top of the form side, as on every printed sheet. */}
        <div className="absolute top-0 left-0 right-0 flex h-1" aria-hidden="true">
          <span className="flex-1 bg-[#003078]" />
          <span className="flex-1 bg-[#c88800]" />
          <span className="flex-1 bg-[#009038]" />
          <span className="flex-1 bg-[#00a0c4]" />
        </div>

        <div
          className={`w-full max-w-sm relative z-10 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {/* Mobile-only branding */}
          <div className="lg:hidden text-center mb-10">
            <div className="flex justify-center mb-4">
              <ClinicLogo size={88} variant="color" />
            </div>
            <h1 className="urdu-text text-2xl text-navy-800 mb-1">
              پروین اسلم میموریل ہسپتال
            </h1>
            <p className="text-gray-500 text-sm">Perveen Aslam Memorial Hospital</p>
          </div>

          {/* Welcome text */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-2">
              Welcome back
            </h2>
            <p className="text-gray-500 text-sm">
              Enter your credentials to access the system
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-5 flex items-start space-x-3 p-4 bg-red-50 border border-red-100 rounded-xl">
              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-600 text-xs font-bold">!</span>
              </div>
              <p className="text-sm text-red-700 leading-snug">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Username
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-[18px] w-[18px] text-gray-400 group-focus-within:text-navy-700 transition-colors" />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  autoComplete="username"
                  autoFocus
                  className="block w-full pl-11 pr-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 text-sm focus:ring-0 focus:border-navy-600 transition-colors"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-[18px] w-[18px] text-gray-400 group-focus-within:text-navy-700 transition-colors" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="block w-full pl-11 pr-12 py-3.5 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 text-sm focus:ring-0 focus:border-navy-600 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-navy-700 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center py-3.5 px-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-semibold rounded-xl shadow-lg shadow-emerald-900/20 hover:shadow-xl hover:shadow-emerald-900/30 hover:from-emerald-700 hover:to-emerald-800 focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-lg text-sm tracking-wide"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </div>
          </form>

          {/* Bottom divider & info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-center text-gray-400 text-xs">
              Contact your administrator if you need access
            </p>
          </div>

          {/* Mobile footer */}
          <p className="lg:hidden text-center text-gray-400 text-xs mt-8">
            &copy; {new Date().getFullYear()} Perveen Aslam Memorial Hospital
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
